import { useState, useCallback, useRef, useEffect } from 'react';
import type { PaymentStatusResponse } from '../types';
import { ApiService, ApiError } from '../services/api';

interface UsePaymentStatusOptions {
  apiService: ApiService | null;
  conversationId: string | null;
  sessionId: string | null;
  pollInterval?: number; // Default: 3000ms
  maxDuration?: number;  // Default: 300000ms (5 minutes)
  onPaymentComplete?: (status: PaymentStatusResponse) => void;
  onPaymentFailed?: (status: PaymentStatusResponse) => void;
  onTimeout?: () => void;
}

interface UsePaymentStatusReturn {
  status: PaymentStatusResponse | null;
  isPolling: boolean;
  startPolling: () => void;
  stopPolling: () => void;
  error: string | null;
  resetError: () => void;
}

const DEFAULT_POLL_INTERVAL = 3000; // 3 seconds
const DEFAULT_MAX_DURATION = 300000; // 5 minutes

export function usePaymentStatus({
  apiService,
  conversationId,
  sessionId,
  pollInterval = DEFAULT_POLL_INTERVAL,
  maxDuration = DEFAULT_MAX_DURATION,
  onPaymentComplete,
  onPaymentFailed,
  onTimeout,
}: UsePaymentStatusOptions): UsePaymentStatusReturn {
  const [status, setStatus] = useState<PaymentStatusResponse | null>(null);
  const [isPolling, setIsPolling] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const pollIntervalRef = useRef<number | null>(null);
  const timeoutRef = useRef<number | null>(null);
  const startTimeRef = useRef<number | null>(null);

  const clearTimers = useCallback(() => {
    if (pollIntervalRef.current !== null) {
      window.clearInterval(pollIntervalRef.current);
      pollIntervalRef.current = null;
    }
    if (timeoutRef.current !== null) {
      window.clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
  }, []);

  const stopPolling = useCallback(() => {
    clearTimers();
    setIsPolling(false);
    startTimeRef.current = null;
  }, [clearTimers]);

  const checkStatus = useCallback(async () => {
    if (!apiService || !conversationId || !sessionId) {
      return;
    }

    try {
      const response = await apiService.checkPaymentStatus(conversationId, sessionId);
      setStatus(response);

      // Check for terminal states
      if (response.paymentStatus === 'paid') {
        stopPolling();
        onPaymentComplete?.(response);
        return;
      }

      if (response.paymentStatus === 'failed' || response.status === 'failed' || response.status === 'cancelled') {
        stopPolling();
        onPaymentFailed?.(response);
        return;
      }

      // Check for timeout
      if (startTimeRef.current && Date.now() - startTimeRef.current >= maxDuration) {
        stopPolling();
        onTimeout?.();
        return;
      }
    } catch (err) {
      const errorMessage = err instanceof ApiError
        ? err.message
        : 'Failed to check payment status';
      setError(errorMessage);
      // Don't stop polling on transient errors
    }
  }, [apiService, conversationId, sessionId, maxDuration, stopPolling, onPaymentComplete, onPaymentFailed, onTimeout]);

  const startPolling = useCallback(() => {
    if (!apiService || !conversationId || !sessionId) {
      setError('Cannot start polling: missing API service, conversation ID, or session ID');
      return;
    }

    if (isPolling) {
      return;
    }

    setError(null);
    setIsPolling(true);
    startTimeRef.current = Date.now();

    // Initial check
    checkStatus();

    // Set up polling interval
    pollIntervalRef.current = window.setInterval(() => {
      checkStatus();
    }, pollInterval);

    // Set up timeout
    timeoutRef.current = window.setTimeout(() => {
      stopPolling();
      onTimeout?.();
    }, maxDuration);
  }, [apiService, conversationId, sessionId, isPolling, pollInterval, maxDuration, checkStatus, stopPolling, onTimeout]);

  const resetError = useCallback(() => {
    setError(null);
  }, []);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      clearTimers();
    };
  }, [clearTimers]);

  // Stop polling if conversation ID changes
  useEffect(() => {
    if (isPolling && !conversationId) {
      stopPolling();
    }
  }, [conversationId, isPolling, stopPolling]);

  return {
    status,
    isPolling,
    startPolling,
    stopPolling,
    error,
    resetError,
  };
}

export type { UsePaymentStatusReturn };
